/**
 * Razorpay + modal libraries (Radix / Headless UI / etc.) interop helper.
 *
 * Problem
 * -------
 * When Razorpay Checkout is opened from inside a Radix Dialog (or any modal
 * library that uses a "focus trap"), the modal library sets
 *   aria-hidden="true"          and
 *   style="pointer-events: none"
 * on EVERY sibling DOM node of its content — which includes Razorpay's
 * injected checkout container.
 *
 * Effect: on mobile, the Razorpay popup looks visible but is completely
 * frozen (no clicks register on UPI / Cards / QR / Continue, etc.).
 *
 * Fix
 * ---
 * Before / after opening Razorpay, force the checkout container and the
 * <body> back into a clickable state. We poll for a short window because
 * Razorpay injects its DOM asynchronously after the script runs.
 */
const RAZORPAY_CONTAINER_SELECTORS = [
  '.razorpay-container',
  '.razorpay-checkout-frame',
  '.razorpay-payment-button',
  '[data-razorpay-checkout]',
  // Razorpay v2 sometimes uses these:
  '.razorpay-backdrop',
  '#razorpay-checkout-frame',
];

const unblock = (el) => {
  if (!el) return;
  try {
    el.removeAttribute('aria-hidden');
    el.removeAttribute('data-aria-hidden');
    if (el.style) {
      el.style.pointerEvents = 'auto';
    }
  } catch (_) {
    /* defensive */
  }
};

const unblockAllRazorpay = () => {
  RAZORPAY_CONTAINER_SELECTORS.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      unblock(el);
      // also walk up the tree — sometimes the ancestor is the one blocked
      let p = el.parentElement;
      let depth = 0;
      while (p && p !== document.body && depth < 6) {
        if (p.getAttribute && (p.getAttribute('aria-hidden') === 'true' || p.style?.pointerEvents === 'none')) {
          unblock(p);
        }
        p = p.parentElement;
        depth += 1;
      }
    });
  });
  // body + html should always be clickable while Razorpay is open. Radix
  // Dialog with `modal=true` sets `pointer-events: none` on <body> — we
  // override it back to `auto` so Razorpay's iframe (a body child) receives
  // touches on mobile.
  try {
    if (document.body && document.body.style) {
      document.body.style.pointerEvents = 'auto';
    }
    if (document.documentElement && document.documentElement.style) {
      document.documentElement.style.pointerEvents = 'auto';
    }
  } catch (_) { /* noop */ }
};

let mutationObserver = null;

/**
 * Call this RIGHT BEFORE `rzp.open()`. It schedules a series of short fixes
 * over the next ~3 seconds (Razorpay injects DOM asynchronously) and also
 * installs a MutationObserver that proactively unblocks Razorpay nodes if
 * a modal library re-applies aria-hidden/pointer-events:none.
 *
 * Returns a cleanup function — call it from Razorpay's `ondismiss` /
 * `handler` so the observer doesn't keep running forever.
 */
export const ensureRazorpayClickable = () => {
  unblockAllRazorpay();
  // Repeat for the first few seconds — Razorpay injects in stages
  const timeouts = [0, 50, 150, 300, 600, 1000, 1500, 2500].map((ms) =>
    window.setTimeout(unblockAllRazorpay, ms)
  );

  // MutationObserver — keeps Razorpay clickable even if some library
  // re-injects aria-hidden / pointer-events:none on it.
  if (mutationObserver) {
    try { mutationObserver.disconnect(); } catch (_) { /* noop */ }
  }
  mutationObserver = new MutationObserver(() => unblockAllRazorpay());
  try {
    // Watch body subtree for sibling re-mutations
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'style'],
    });
    // ALSO watch <html> attribute changes — Radix sometimes sets
    // pointer-events:none on documentElement during dialog open.
    mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });
  } catch (_) {
    /* defensive */
  }

  return () => {
    timeouts.forEach((t) => window.clearTimeout(t));
    if (mutationObserver) {
      try { mutationObserver.disconnect(); } catch (_) { /* noop */ }
      mutationObserver = null;
    }
  };
};
